import { Button, Stack, Text, TextInput } from "@mantine/core";
import { useEffect, useState } from "react";
import {
  checkDisplayNameAvailability,
  createOrUpdateProfile,
} from "../../api-clients/iwannacamp-api";
import useIdToken from "../../hooks/useIdToken";
import { createLogger } from "../../utils/logger";

const logger = createLogger("DisplayNamePrompt");

interface DisplayNamePromptProps {
  onSuccess?: () => void;
  onSkip?: () => void;
  allowSkip?: boolean;
}

const DisplayNamePrompt = ({
  onSuccess,
  onSkip,
  allowSkip = false,
}: DisplayNamePromptProps) => {
  const idToken = useIdToken();
  const [displayName, setDisplayName] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Debounced availability check
  useEffect(() => {
    if (!displayName || displayName.length < 3) {
      setIsAvailable(null);
      return;
    }

    const timer = setTimeout(async () => {
      if (!idToken) {
        return;
      }

      setIsChecking(true);
      setError(null);
      try {
        const response = await checkDisplayNameAvailability({
          path: { display_name: displayName },
          headers: { Authorization: `Bearer ${idToken}` },
        });

        if (response.data) {
          setIsAvailable(response.data.available);
          if (!response.data.available) {
            setError("Display name is already taken");
          }
        }
      } catch (err) {
        logger.error("Error checking display name:", err);
      } finally {
        setIsChecking(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [displayName, idToken]);

  const handleSubmit = async () => {
    if (!idToken || !displayName || !isAvailable) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await createOrUpdateProfile({
        body: { display_name: displayName },
        headers: { Authorization: `Bearer ${idToken}` },
      });

      if (response.error) {
        const errorMessage =
          typeof response.error === "object" &&
          response.error !== null &&
          "detail" in response.error
            ? String(response.error.detail)
            : "Failed to set display name";
        setError(errorMessage);
        return;
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      logger.error("Error setting display name:", err);
      setError(err.message || "Failed to set display name");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    }
  };

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Choose a display name that will appear on your reviews. You can change
        this later in your profile settings.
      </Text>

      <TextInput
        label="Display Name"
        placeholder="CampingEnthusiast"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        error={error}
        description={
          isChecking
            ? "Checking availability..."
            : isAvailable === true
            ? "✓ Available"
            : isAvailable === false
            ? "✗ Taken"
            : "3-20 characters, letters, numbers, spaces, underscores, hyphens"
        }
        disabled={isSubmitting}
        minLength={3}
        maxLength={20}
      />

      <Stack gap="xs">
        <Button
          onClick={handleSubmit}
          disabled={
            !displayName ||
            displayName.length < 3 ||
            !isAvailable ||
            isChecking ||
            isSubmitting
          }
          loading={isSubmitting}
          fullWidth
        >
          Set Display Name
        </Button>

        {allowSkip && (
          <Button
            variant="subtle"
            onClick={handleSkip}
            disabled={isSubmitting}
            fullWidth
          >
            Skip for now
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

export default DisplayNamePrompt;
